import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';

import { AppLayout } from '@/common/components/layout/AppLayout';

import { AppLayoutComposer } from './AppLayoutComposer';
import { flattenRoutes } from './flattenRoutes';
import { LandingRedirect } from './LandingRedirect';
import { RouteGuard } from './RouteGuard';
import { routes as routeDefinitions } from './routes';
import type { RouteConfig } from './types';

const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

/** Loading state shown while a lazy page/layout chunk downloads. */
const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center">
    <p className="text-muted-foreground text-sm">Loading...</p>
  </div>
);

/**
 * Main application router (gpm-webapp-v2 pattern, adapted for reipm).
 *
 * - Flattens the nested `routes` tree (accumulated layouts + concatenated
 *   paths) into flat `<Route>`s, nesting layouts via {@link AppLayoutComposer}.
 * - Renders only *authenticated* content: AccessProvider gates the whole
 *   router and shows the login page as its fallback, so there is no login
 *   route here (unlike gpm). `noRootLayout` routes are still supported for
 *   future public pages.
 * - {@link RouteGuard} re-checks `routeAccess` permissions on every
 *   navigation; denied paths render the 404 page.
 * - `/` and unknown paths redirect to the landing page
 *   ({@link LandingRedirect}); dead route leaves render 404.
 */
export const AppRouter = () => {
  const routes: RouteConfig[] = routeDefinitions;
  const flatRoutes = flattenRoutes(routes);

  return (
    <Suspense fallback={<PageLoader />}>
      <RouteGuard>
        <Routes>
          {flatRoutes
            .filter(({ noRootLayout }) => noRootLayout)
            .map(({ path, element: Page }) => (
              <Route key={path} path={path} element={Page ? <Page /> : <NotFoundPage />} />
            ))}

          <Route element={<AppLayout />}>
            {flatRoutes
              .filter(({ noRootLayout }) => !noRootLayout)
              .map(({ path, element: Page, layouts }) => {
                let content;
                if (Page) {
                  content = <Page />;
                } else if (layouts && layouts.length > 0) {
                  content = null;
                } else {
                  content = <NotFoundPage />;
                }

                return (
                  <Route
                    key={path}
                    path={path}
                    element={<AppLayoutComposer nested={layouts}>{content}</AppLayoutComposer>}
                  />
                );
              })}
          </Route>

          <Route path="*" element={<LandingRedirect />} />
        </Routes>
      </RouteGuard>
    </Suspense>
  );
};
